import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clampAndWrapIdentity, expandIdentityString } from "../identityFilterUtils.ts";
import {
  ChevronIcon,
  collectIdentityMatches,
  FloatingPopover,
  GridIcon,
  HashIcon,
  IdChip,
  OPERATOR_DEFAULT_LABEL,
  SearchIcon,
  SuggestRow,
  XIcon,
  getInitialIdText,
  getNoun,
  groupValuesSorted,
  sharedStyles as s,
  useGroupData,
  useIdentityEmit,
  useItemById,
  type VariantProps,
} from "./shared.tsx";

type Tab = "search" | "id" | "bulk";


/**
 * Variation B — "Compact pills".
 * Filter collapses to a single pill showing the current count. Click opens a
 * tabbed popover (Search / By ID / Bulk) for editing.
 */
export function CompactVariant<T>({
  config,
  columnName,
  isParameter,
  value,
  onChange,
  onSearch,
}: VariantProps<T>) {
  const [idText, setIdText] = useState<string>(() => getInitialIdText(value));
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("search");
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string>(config.groupFields?.[0]?.field ?? "");
  /* Selections keep one checked-value set per group field (not just the
     active tab) so choosing e.g. Retail Host AND Type narrows the result to
     kiosks matching both (AND across fields, OR within a field's values)
     instead of unioning every field's matches into one giant list. */
  const [groupSelections, setGroupSelections] = useState<Map<string, Set<string>>>(new Map());
  const [bulkSearch, setBulkSearch] = useState("");

  const emit = useIdentityEmit(columnName, isParameter, onChange);
  const groupData = useGroupData(config);
  const byId = useItemById(config);
  const noun = getNoun(config);

  useEffect(() => {
    if (value.length === 0) {
      setIdText("");
    } else if (value[0]?.oValue != null) {
      setIdText(String(value[0].oValue));
    }
  }, [value]);

  const ids = useMemo(() => expandIdentityString(idText), [idText]);

  const update = useCallback(
    (text: string) => {
      setIdText(text);
      emit(text);
    },
    [emit],
  );

  const toggleId = useCallback(
    (id: number) => {
      const current = expandIdentityString(idText);
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      update(clampAndWrapIdentity(next));
    },
    [idText, update],
  );

  const browsing = !search.trim();
  // An empty search lists everything, oldest first by id so the newest is at the
  // bottom, and the tab works as a dropdown; typing filters it as before, counting
  // every match past the render cap.
  const matches = useMemo(
    () => (browsing
      ? { shown: [...config.items].sort((a, b) => config.getIdFromItem(a) - config.getIdFromItem(b)), total: 0, allIds: [] as number[] }
      : collectIdentityMatches(config, search, 60)),
    [browsing, search, config],
  );
  const suggestions = matches.shown;

  const groupValues = useMemo(
    () => groupValuesSorted(groupData, selectedGroup),
    [groupData, selectedGroup],
  );

  const filteredGroupValues = useMemo(() => {
    if (!bulkSearch.trim()) return groupValues;
    const lc = bulkSearch.toLowerCase();
    return groupValues.filter((g) => g.value.toLowerCase().includes(lc));
  }, [groupValues, bulkSearch]);

  const toggleGroupValue = useCallback((field: string, val: string) => {
    setGroupSelections((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(field));
      if (set.has(val)) set.delete(val); else set.add(val);
      if (set.size) next.set(field, set); else next.delete(field);
      return next;
    });
  }, []);

  /* AND across fields, OR within a field: union the ids matching any checked
     value of a given field, then intersect those per-field sets together. */
  const bulkMatchIds = useMemo(() => {
    if (!groupData || groupSelections.size === 0) return [] as number[];
    let result: Set<number> | null = null;
    for (const [field, vals] of groupSelections) {
      const m = groupData.get(field);
      if (!m) return [];
      const fieldIds = new Set<number>();
      for (const v of vals) {
        const list = m.get(v);
        if (list) for (const id of list) fieldIds.add(id);
      }
      if (result === null) result = fieldIds;
      else {
        const next = new Set<number>();
        for (const id of result) if (fieldIds.has(id)) next.add(id);
        result = next;
      }
    }
    return result ? [...result] : [];
  }, [groupData, groupSelections]);

  const applyBulk = useCallback(() => {
    if (!bulkMatchIds.length) return;
    update(clampAndWrapIdentity([...expandIdentityString(idText), ...bulkMatchIds]));
    setGroupSelections(new Map());
  }, [bulkMatchIds, idText, update]);

  const close = useCallback(() => setOpen(false), []);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const hasIds = ids.length > 0;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-0-5)" }}>
      <button
        ref={anchorRef}
        type="button"
        className={hasIds ? s.btnPillActive : s.btn}
        onClick={() => setOpen((o) => !o)}
      >
        <HashIcon />
        {hasIds ? (
          <>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{ids.length}</span>
            <span>selected</span>
          </>
        ) : (
          <span style={{ color: "var(--color-text-muted)" }}>Any {noun}</span>
        )}
        <ChevronIcon />
      </button>
      {hasIds && (
        <button type="button" className={s.btnGhost} onClick={() => update("")} title="Clear" aria-label="Clear">
          <XIcon size={11} />
        </button>
      )}

      <FloatingPopover open={open} anchorRef={anchorRef} onClose={close} width={440} className={s.popover}>
        <div>
          <div className={s.tabBar}>
            {renderTab("search", "Search", SearchIcon)}
            {renderTab("id", "By ID", HashIcon)}
            {config.groupFields && renderTab("bulk", "Bulk", GridIcon)}
          </div>

          {tab === "search" && (
            <div>
              <div style={{ padding: "var(--space-2)", borderBottom: "1px solid var(--color-bg-muted)" }}>
                <input
                  autoFocus
                  className={s.input}
                  style={{ width: "100%", height: 32 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    // Never on an empty search: that would add every listed item.
                    if (e.key === "Enter" && !browsing && matches.total) {
                      e.preventDefault();
                      // Enter = add ALL matches — every one, not just the rows
                      // that fit the render cap. Doesn't fire parent search.
                      update(clampAndWrapIdentity([...ids, ...matches.allIds]));
                      setSearch("");
                    }
                  }}
                  placeholder={`Search ${noun}s…`}
                />
              </div>
              {search.trim() && suggestions.length > 0 && (
                <div style={{
                  padding: "var(--space-1) var(--space-2)", fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  borderBottom: "1px solid var(--color-bg-muted)",
                  background: "var(--color-bg-muted)",
                  display: "flex", justifyContent: "space-between",
                }}>
                  <span>
                    {matches.total} match{matches.total === 1 ? "" : "es"}
                    {matches.total > suggestions.length && ` · showing first ${suggestions.length}`}
                  </span>
                  <span>Press <span className={s.kbd}>↵</span> to add all {matches.total}</span>
                </div>
              )}
              {browsing && config.items.length > 0 && (
                <div style={{
                  padding: "var(--space-1) var(--space-2)", fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  borderBottom: "1px solid var(--color-bg-muted)",
                  background: "var(--color-bg-muted)",
                }}>
                  {`${config.items.length} ${noun}${config.items.length === 1 ? "" : "s"}, newest last — type to filter by name, city or address`}
                </div>
              )}
              <div style={{ maxHeight: 280, overflow: "auto" }}>
                {browsing && config.items.length === 0 && (
                  <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>
                    No {noun}s.
                  </div>
                )}
                {search.trim() && suggestions.length === 0 && (
                  <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>
                    No matches.
                  </div>
                )}
                {suggestions.map((it, i) => {
                  const id = config.getIdFromItem(it);
                  return (
                    <SuggestRow
                      key={i}
                      item={it}
                      config={config}
                      selected={ids.includes(id)}
                      showCheck
                      onClick={() => toggleId(id)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {tab === "id" && (
            <div style={{ padding: "var(--space-3)" }}>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: "var(--space-1)" }}>
                Enter IDs and ranges separated by commas.
              </div>
              <input
                autoFocus
                className={s.input}
                style={{ width: "100%", height: 36, fontSize: "var(--text-base)", fontVariantNumeric: "tabular-nums" }}
                value={idText}
                onChange={(e) => update(e.target.value.replace(/[^0-9,\-]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
                placeholder="1, 3, 5-10, 42"
              />
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: "var(--space-1)" }}>
                {ids.length > 0 ? `${ids.length} ${noun}${ids.length === 1 ? "" : "s"}` : "No IDs entered"}
              </div>
              {ids.length > 0 && (
                <div style={{ marginTop: "var(--space-2)", display: "flex", flexWrap: "wrap", gap: "var(--space-0-5)", maxHeight: 120, overflow: "auto" }}>
                  {ids.slice(0, 30).map((id) => (
                    <IdChip key={id} id={id} item={byId.get(id)} config={config} onRemove={() => toggleId(id)} />
                  ))}
                  {ids.length > 30 && <IdChip id={0} item={undefined} config={config} dim />}
                </div>
              )}
            </div>
          )}

          {tab === "bulk" && config.groupFields && (
            <div style={{ padding: "var(--space-2)" }}>
              <div className={s.seg} style={{ flexWrap: "wrap", marginBottom: "var(--space-2)" }}>
                {config.groupFields.map((gf) => (
                  <button
                    type="button"
                    key={gf.field}
                    className={selectedGroup === gf.field ? s.segBtnActive : s.segBtn}
                    onClick={() => { setSelectedGroup(gf.field); setBulkSearch(""); }}
                  >
                    {gf.label}
                    {groupSelections.get(gf.field)?.size ? (
                      <span className={s.countBadge}>{groupSelections.get(gf.field)!.size}</span>
                    ) : null}
                  </button>
                ))}
              </div>
              {groupSelections.size > 1 && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: "var(--space-1)" }}>
                  Matching all selected fields (AND).
                </div>
              )}
              <input
                className={s.input}
                style={{ width: "100%", height: 28, marginBottom: "var(--space-1)" }}
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
                placeholder={`Filter ${groupValues.length} value${groupValues.length === 1 ? "" : "s"}…`}
              />
              <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)" }}>
                {filteredGroupValues.map((g) => (
                  <label key={g.value} className={s.check}>
                    <input
                      type="checkbox"
                      checked={groupSelections.get(selectedGroup)?.has(g.value) ?? false}
                      onChange={() => toggleGroupValue(selectedGroup, g.value)}
                    />
                    <span style={g.value === OPERATOR_DEFAULT_LABEL ? { fontStyle: "italic" } : undefined}>{g.value}</span>
                    <span className={s.countBadge}>{g.count}</span>
                  </label>
                ))}
                {!filteredGroupValues.length && (
                  <div style={{ padding: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                    {groupValues.length ? "No matches." : "No values."}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "var(--space-2)" }}>
                <button type="button" className={s.btnPrimary} onClick={applyBulk} disabled={!bulkMatchIds.length}>
                  Add {bulkMatchIds.length || ""} {noun}s
                </button>
              </div>
            </div>
          )}

          <div className={s.popFooter}>
            <span style={{ color: "var(--color-text-muted)" }}>{ids.length} total</span>
            <div className={s.spacer} />
            {ids.length > 0 && (
              <button type="button" className={s.btnGhost} onClick={() => update("")}>
                Clear
              </button>
            )}
            <button
              type="button"
              className={s.btn}
              onClick={() => {
                // Values ticked in Bulk but not yet added count as chosen: Done
                // applies them rather than silently dropping them.
                const pending = bulkMatchIds.length > 0;
                if (pending) applyBulk();
                setOpen(false);
                // After applyBulk the new ids reach useGridData's filtersRef only
                // once React commits, so defer the search like inlineVariant does.
                if (pending) requestAnimationFrame(() => onSearch());
                else onSearch();
              }}
            >
              {bulkMatchIds.length > 0 ? `Add ${bulkMatchIds.length} & done` : "Done"}
            </button>
          </div>
        </div>
      </FloatingPopover>
    </div>
  );

  function renderTab(key: Tab, label: string, Icon: React.ComponentType<{ size?: number }>) {
    return (
      <button
        type="button"
        className={tab === key ? s.tabActive : s.tab}
        onClick={() => setTab(key)}
      >
        <Icon size={12} /> {label}
      </button>
    );
  }
}
